import { type ReadonlyJSONValue, type Row, type Transaction } from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import { zql, type Context } from "../../zero/schema";
import { z } from "zod/v4";
import { withZeroTransaction } from "../../zero/utils";
import { type UIMessage, type UIMessageChunk, asSchema } from "ai";
import { oc, type, eventIterator } from "@orpc/contract";
import type { AnyClientAgent, BaseAgent, InferAgentContext } from "../agent-definition/agent";
import { accessDenied, createId } from "@corpuscomplete/core/utils";
import type { AgentRegistry } from "../agent-definition";
import { getProjectToolLifecycle } from "../agent/tool-lifecycle";

/**
 * Agent session boundary for chat lifecycle, tool-call state, and permissions.
 *
 * Client-facing session/message contracts live here; server-only streaming and
 * post-commit orchestration stay in API adapters.
 *
 * @see `packages/core/docs/ai/agents.md`
 */
export namespace AgentSession {
  export type RawInfo = Row["agentSession"];

  export function normalizeSession(row: Row["agentSession"]): RawInfo {
    return row;
  }

  export const TERMINAL_TOOL_PART_STATES = [
    "output-available",
    "output-error",
    "output-canceled",
  ] as const;

  export type TerminalToolPartState = (typeof TERMINAL_TOOL_PART_STATES)[number];
  type BaseToolPart = Extract<UIMessage["parts"][number], { type: `tool-${string}` }>;
  type BaseToolPartState = BaseToolPart extends { state: infer State extends string }
    ? State
    : never;

  export type NonTerminalToolPartState = Exclude<BaseToolPartState, TerminalToolPartState>;
  export type ToolPartState = NonTerminalToolPartState | TerminalToolPartState;

  export type OutputCanceledToolPart = Omit<BaseToolPart, "state" | "output"> & {
    state: "output-canceled";
    output?: { status: "canceled" };
  };

  export type ToolPart = BaseToolPart | OutputCanceledToolPart;

  export type MessagePart =
    | Exclude<UIMessage["parts"][number], { type: `tool-${string}` }>
    | ToolPart;
  export type MessageParts = MessagePart[];

  export function isTerminalToolPartState(state: string): state is TerminalToolPartState {
    return TERMINAL_TOOL_PART_STATES.includes(state as TerminalToolPartState);
  }

  export function isAgentToolPart(part: MessagePart): part is ToolPart {
    return typeof part.type === "string" && part.type.startsWith("tool-") && "state" in part;
  }

  export function isPendingToolPart(part: MessagePart): part is ToolPart {
    if (!isAgentToolPart(part)) {
      return false;
    }
    return !isTerminalToolPartState(part.state);
  }

  export function toOutputCanceledToolPart(part: ToolPart): ToolPart {
    const { output: _output, ...partWithoutOutput } = part;
    return {
      ...partWithoutOutput,
      state: "output-canceled",
    };
  }

  export const messagesBySessionId = defineQuery(
    z.object({
      agentType: z.string().max(100),
      agentVersion: z.number().int().min(1),
      sessionId: z.uuid(),
      limit: z.number().min(1).max(50).optional(),
    }),
    ({ ctx, args: { agentType, agentVersion, sessionId, limit = 20 } }) => {
      if (!ctx.agentRegistry.hasVersion(agentType, agentVersion)) {
        // Return an empty query (do not leak whether a session exists).
        return zql.agentSessionMessage
          .where("sessionId", "=", sessionId)
          .where("id", "=", "00000000-0000-0000-0000-000000000000")
          .limit(limit);
      }

      const agent = ctx.agentRegistry.getAgent(agentType, agentVersion);
      return zql.agentSessionMessage
        .where("sessionId", "=", sessionId)
        .where("role", "!=", "system")
        .orderBy("timeCreated", "desc")
        .limit(limit)
        .whereExists("session", (s) =>
          s
            .where("agentType", "=", agentType)
            .where("agentVersion", "=", agentVersion)
            .where((e) => agent.viewable(e, ctx.actor)),
        );
    },
  );

  export const messagesForChatHandler = async (
    tx: Transaction,
    {
      sessionId,
      messageId,
    }: {
      sessionId: string;
      messageId: string;
    },
  ) => {
    const messages = await tx.run(
      zql.agentSessionMessage
        .orderBy("timeCreated", "asc")
        .where("sessionId", "=", sessionId)
        .where("id", "!=", messageId),
    );

    const filteredMessages = messages.filter(
      (message) => !(message.role === "assistant" && message.status === "canceled"),
    );

    return filteredMessages.sort((a, b) => {
      if (a.role === "system" && b.role !== "system") return -1;
      if (b.role === "system" && a.role !== "system") return 1;
      return Temporal.Instant.compare(a.timeCreated, b.timeCreated);
    });
  };

  export const getSessionById = (tx: Transaction, sessionId: string) => {
    return tx.run(zql.agentSession.where("id", "=", sessionId).one());
  };

  export const getMessageById = (
    tx: Transaction,
    {
      sessionId,
      messageId,
    }: {
      sessionId: string;
      messageId: string;
    },
  ) => {
    return tx.run(
      zql.agentSessionMessage.where("id", "=", messageId).where("sessionId", "=", sessionId).one(),
    );
  };

  export async function insertInitialAssistantMessage(
    tx: Transaction,
    args: { sessionId: string; messageId: string; timeNow: Temporal.Instant },
  ): Promise<void> {
    await tx.mutate.agentSessionMessage.insert({
      id: args.messageId,
      timeCreated: args.timeNow,
      timeUpdated: args.timeNow,
      role: "assistant",
      status: "streaming",
      sessionId: args.sessionId,
      parts: [],
    });
  }

  export type Message = Row["agentSessionMessage"];

  export function normalizeMessage(row: Row["agentSessionMessage"]): Message {
    return row;
  }

  export function normalizeMessages(messages: readonly Row["agentSessionMessage"][]): Message[] {
    return messages.map(normalizeMessage);
  }

  export type SubscribePayload = { chunk: UIMessageChunk };

  export type Info<Agent extends AnyClientAgent> = Omit<Row["agentSession"], "context"> & {
    context: InferAgentContext<Agent> | null;
  };

  export function parse<Agent extends AnyClientAgent>(
    agent: Agent,
    raw: Row["agentSession"],
  ): Info<Agent> {
    const normalizedRaw = normalizeSession(raw);
    const contextResult = agent.context?.schema?.safeParse(normalizedRaw.context);
    const context = contextResult?.success ? contextResult.data : null;

    return { ...normalizedRaw, context };
  }

  export const messageStreamerContract = {
    subscribe: oc.output(eventIterator(type<SubscribePayload>())),
  };

  export function getAgent<Agent extends AnyClientAgent>(
    agentRegistry: AgentRegistry<Agent>,
    agentType: string,
    version?: number,
  ) {
    return agentRegistry.getAgent(agentType, version);
  }

  export function getAgentForSessionVersion<Agent extends AnyClientAgent>(
    agentRegistry: AgentRegistry<Agent>,
    agentType: string,
    agentVersion: number,
  ): Agent {
    if (!agentRegistry.hasVersion(agentType, agentVersion)) {
      throw Object.assign(
        new Error(
          `Agent version ${agentVersion} for "${agentType}" has been deprecated and is no longer available`,
        ),
        { code: "VERSION_DEPRECATED" as const },
      );
    }
    return getAgent(agentRegistry, agentType, agentVersion);
  }

  type CreateInputs<Agent extends AnyClientAgent> =
    Agent extends BaseAgent<any, any, infer Params, any>
      ? Params extends undefined
        ? { params?: undefined }
        : { params: Params }
      : { params?: undefined };

  export const createArgs = <Agent extends AnyClientAgent>(
    agent: Agent,
    args: CreateInputs<Agent>,
  ) => {
    return {
      ...args,
      agentType: agent.agentType,
      agentVersion: agent.version,
      sessionId: createId(),
    } satisfies z.infer<typeof createSchema>;
  };

  export const createSchema = z.object({
    sessionId: z.uuid(),
    agentType: z.string().max(100),
    agentVersion: z.number().int().min(1),
    params: z.any().optional(),
  });

  export const create = async ({
    tx,
    ctx,
    args: { sessionId, agentType, agentVersion, params: rawParams },
  }: {
    tx: Transaction;
    ctx: Context;
    args: z.infer<typeof createSchema>;
  }) => {
    const { agent, params, context } = await createBase({
      tx,
      ctx,
      args: { sessionId, agentType, agentVersion, params: rawParams },
    });

    const agentPreCommitResult = await runOnCreatePreCommit({
      tx,
      ctx,
      agent,
      args: { sessionId, params, context },
    });

    return {
      agent,
      agentPreCommitResult,
      params,
      context,
    };
  };

  export const createBase = async ({
    tx,
    ctx,
    args: { sessionId, agentType, agentVersion, params: rawParams },
  }: {
    tx: Transaction;
    ctx: Context;
    args: z.infer<typeof createSchema>;
  }) => {
    const timeNow = Temporal.Now.instant();
    const agent = ctx.agentRegistry.getAgent(agentType, agentVersion);

    const params = agent.paramsSchema?.parse(rawParams) ?? rawParams;

    const canCreate = await agent.canCreate?.({
      tx,
      ctx,
      args: { params },
    });
    if (!canCreate) {
      accessDenied("Cannot create agent session");
    }

    const resolvedContext = agent.context
      ? await agent.context.resolve({ tx, ctx, args: { params } })
      : undefined;
    const context = agent.context?.schema.parse(resolvedContext) ?? resolvedContext;

    await tx.mutate.agentSession.insert({
      id: sessionId,
      timeCreated: timeNow,
      agentType,
      agentVersion,
      context: context as ReadonlyJSONValue | undefined,
    });

    return { agent, params, context };
  };

  export const runOnCreatePreCommit = async ({
    tx,
    ctx,
    agent,
    args: { sessionId, params, context },
  }: {
    tx: Transaction;
    ctx: Context;
    agent: AnyClientAgent;
    args: {
      sessionId: string;
      params: unknown;
      context: unknown;
    };
  }) => {
    const agentPreCommitResult: unknown = await agent.hooks.onCreate?.preCommit?.({
      tx,
      ctx,
      args: { sessionId, params, context },
    });

    return agentPreCommitResult;
  };

  export type SendMessage = z.infer<typeof sendMessage.schema>;
  export const sendMessage = withZeroTransaction(
    z.object({
      agentType: z.string().max(100),
      messageId: z.uuid(),
      text: z.string().max(5_000),
      sessionId: z.uuid(),
      responseMessageId: z.uuid(),
    }),
    async ({ tx, ctx, args }) => {
      const timeNow = Temporal.Now.instant();

      // Fetch session to get the stored version
      const session = await tx.run(zql.agentSession.where("id", "=", args.sessionId).one());
      if (!session) {
        accessDenied("Session not found");
      }

      if (session.agentType !== args.agentType) {
        accessDenied("Session agentType mismatch");
      }

      const agent = getAgentForSessionVersion(
        ctx.agentRegistry,
        args.agentType,
        session.agentVersion,
      );

      const canEdit = await agent.canEdit?.({
        tx,
        ctx,
        args: { sessionId: args.sessionId },
      });
      if (!canEdit) {
        accessDenied("Cannot send message to agent session");
      }

      if (!agent.chat) {
        throw new Error("Agent does not support chat");
      }

      const lastAssistantMessage = await tx.run(
        zql.agentSessionMessage
          .where("sessionId", "=", args.sessionId)
          .where("role", "=", "assistant")
          .orderBy("timeCreated", "desc")
          .one(),
      );
      const lastStatus = lastAssistantMessageStatus(
        lastAssistantMessage ? normalizeMessage(lastAssistantMessage) : undefined,
      );
      if (lastStatus === "streaming") {
        throw new Error("Cannot send message while last assistant message is streaming");
      }
      if (lastStatus === "pending_tool_call") {
        throw new Error("Cannot send message while last assistant message is pending tool call");
      }

      await tx.mutate.agentSessionMessage.insert({
        id: args.messageId,
        timeCreated: timeNow,
        timeUpdated: timeNow,
        status: "done",
        sessionId: args.sessionId,
        role: "user",
        userId: ctx.actor.userId,
        parts: [{ type: "text", text: args.text }],
      });

      await tx.mutate.agentSessionMessage.insert({
        id: args.responseMessageId,
        timeCreated: timeNow.add({ seconds: 1 }),
        timeUpdated: timeNow.add({ seconds: 1 }),
        status: "streaming",
        sessionId: args.sessionId,
        role: "assistant",
        parts: [],
      });
    },
  );

  function hasPendingToolCalls(message: Message): boolean {
    if (!message) {
      return false;
    }
    return message.parts.some(isPendingToolPart);
  }

  /**
   * Get the effective status of the last assistant message, accounting for pending tool calls.
   */
  export function lastAssistantMessageStatus(
    message: Message | undefined,
  ): Message["status"] | "pending_tool_call" {
    if (!message) {
      return "done";
    }
    if (hasPendingToolCalls(message)) {
      return "pending_tool_call";
    }
    return message.status;
  }

  export function getPendingToolPartsFromParts(parts: MessageParts | undefined): MessageParts {
    if (!parts) {
      return [];
    }

    return parts.filter(isPendingToolPart);
  }

  export const addToolCallOutputSchema = z.object({
    agentType: z.string().max(100),
    sessionId: z.uuid(),
    toolName: z.string().max(100),
    toolCallId: z.string().max(500),
    toolOutput: z.any(),
    messageId: z.uuid(),
  });

  export type AddToolCallOutputResult = {
    toolName: string;
    toolInput: ReadonlyJSONValue;
    toolOutput: ReadonlyJSONValue;
    continueGeneration: boolean;
    continuationMessageId?: string;
  };

  export const addToolCallOutput = async ({
    tx,
    ctx,
    args,
  }: {
    tx: Transaction;
    ctx: Context;
    args: z.infer<typeof addToolCallOutputSchema>;
  }): Promise<AddToolCallOutputResult> => {
    const timeNow = Temporal.Now.instant();

    // Fetch session to get the stored version
    const session = await tx.run(zql.agentSession.where("id", "=", args.sessionId).one());
    if (!session) {
      accessDenied("Session not found");
    }

    if (session.agentType !== args.agentType) {
      accessDenied("Session agentType mismatch");
    }

    const agent = getAgentForSessionVersion(
      ctx.agentRegistry,
      args.agentType,
      session.agentVersion,
    );
    const parsedSession = parse(agent, session);
    const resolvedTools =
      agent.chat?.resolveTools?.({ context: parsedSession.context }) ?? agent.chat?.tools;

    const canEdit = await agent.canEdit({ tx, ctx, args: { sessionId: args.sessionId } });
    if (!canEdit) {
      accessDenied("Cannot add tool output to agent session");
    }

    if (!agent.chat) {
      throw new Error("Agent does not support chat");
    }

    const message = await tx.run(
      zql.agentSessionMessage
        .where("id", "=", args.messageId)
        .where("sessionId", "=", args.sessionId)
        .where("role", "=", "assistant")
        .one(),
    );
    if (!message) {
      accessDenied("Message not found");
    }

    let toolInput: ReadonlyJSONValue | undefined;
    let parsedToolOutput: ReadonlyJSONValue | undefined;
    let resolvedToolName: string | undefined;
    let toolCallFound = false;
    const newParts: typeof message.parts = [];
    for (const part of message.parts) {
      if (isAgentToolPart(part) && part.toolCallId === args.toolCallId) {
        toolCallFound = true;
        if (part.state !== "input-available") {
          throw new Error("Tool call is not in input-available state");
        }
        resolvedToolName = part.type.slice("tool-".length);
        if (resolvedToolName !== args.toolName) {
          throw new Error("Tool name does not match toolCallId");
        }
        const agentTool = resolvedTools?.[resolvedToolName as keyof typeof resolvedTools];
        if (!agentTool) {
          accessDenied("Tool not found");
        }
        if (!agentTool.outputSchema) {
          throw new Error("Expected tool output schema");
        }
        const agentToolOutputSchema = asSchema(agentTool.outputSchema);
        toolInput = part.input as ReadonlyJSONValue | undefined;
        const validationResult = await agentToolOutputSchema.validate?.(args.toolOutput);
        if (!validationResult?.success) {
          throw new Error(
            `Tool output validation failed: ${validationResult?.error?.message ?? "Unknown error"}`,
          );
        }
        newParts.push({
          ...part,
          output: validationResult.value,
          state: "output-available",
        });
        parsedToolOutput = validationResult.value as ReadonlyJSONValue;
      } else {
        newParts.push(part);
      }
    }

    if (!toolCallFound) {
      accessDenied("Tool call not found");
    }

    if (resolvedToolName === undefined) {
      throw new Error("Invariant violation: resolved tool name is undefined");
    }

    if (toolInput === undefined) {
      throw new Error("Invariant violation: persisted tool input is undefined");
    }

    if (parsedToolOutput === undefined) {
      throw new Error("Invariant violation: validated tool output is undefined");
    }

    const hasRemainingPendingToolCalls = hasPendingToolCalls(
      normalizeMessage({ ...message, parts: newParts }),
    );
    const toolLifecycle = getProjectToolLifecycle(
      resolvedTools?.[resolvedToolName as keyof typeof resolvedTools],
    );
    const shouldContinueGeneration =
      Boolean(toolLifecycle?.continueAfterTool) && !hasRemainingPendingToolCalls;

    let continuationMessageId: string | undefined;
    if (shouldContinueGeneration) {
      continuationMessageId = createId();
      await tx.mutate.agentSessionMessage.insert({
        id: continuationMessageId,
        timeCreated: timeNow.add({ milliseconds: 1 }),
        timeUpdated: timeNow.add({ milliseconds: 1 }),
        status: "streaming",
        sessionId: args.sessionId,
        role: "assistant",
        parts: [],
      });
    }

    await tx.mutate.agentSessionMessage.update({
      id: args.messageId,
      parts: newParts,
      timeUpdated: timeNow,
    });

    return {
      toolName: resolvedToolName,
      toolInput,
      toolOutput: parsedToolOutput,
      continueGeneration: shouldContinueGeneration,
      continuationMessageId,
    };
  };

  export const cancelMessageSchema = z.object({
    agentType: z.string().max(100),
    sessionId: z.uuid(),
    messageId: z.uuid(),
  });
  export const cancelMessage = withZeroTransaction(
    cancelMessageSchema,
    async ({ tx, ctx, args }) => {
      const timeNow = Temporal.Now.instant();

      // Fetch session to get the stored version
      const session = await tx.run(zql.agentSession.where("id", "=", args.sessionId).one());
      if (!session) {
        accessDenied("Session not found");
      }

      if (session.agentType !== args.agentType) {
        accessDenied("Session agentType mismatch");
      }

      const agent = getAgentForSessionVersion(
        ctx.agentRegistry,
        args.agentType,
        session.agentVersion,
      );
      const canEdit = await agent.canEdit?.({
        tx,
        ctx,
        args: { sessionId: args.sessionId },
      });
      if (!canEdit) {
        accessDenied("Cannot cancel message");
      }

      const message = await tx.run(
        zql.agentSessionMessage
          .where("id", "=", args.messageId)
          .where("sessionId", "=", args.sessionId)
          .one(),
      );

      if (!message) {
        accessDenied("Message not found");
      }

      if (message.role !== "assistant") {
        accessDenied("Only assistant messages can be canceled");
      }

      // Idempotent: if the message is no longer streaming, treat cancellation as a no-op.
      if (message.status !== "streaming") {
        return;
      }

      await tx.mutate.agentSessionMessage.update({
        id: args.messageId,
        timeUpdated: timeNow,
        status: "canceled",
      });
    },
  );

  export const cancelToolCallSchema = z.object({
    agentType: z.string().max(100),
    sessionId: z.uuid(),
    messageId: z.uuid(),
    toolCallId: z.string().max(500),
  });

  export const cancelToolCall = withZeroTransaction(
    cancelToolCallSchema,
    async ({ tx, ctx, args }) => {
      const timeNow = Temporal.Now.instant();

      const session = await tx.run(zql.agentSession.where("id", "=", args.sessionId).one());
      if (!session) {
        accessDenied("Session not found");
      }

      if (session.agentType !== args.agentType) {
        accessDenied("Session agentType mismatch");
      }

      const agent = getAgentForSessionVersion(
        ctx.agentRegistry,
        args.agentType,
        session.agentVersion,
      );
      const canEdit = await agent.canEdit?.({
        tx,
        ctx,
        args: { sessionId: args.sessionId },
      });
      if (!canEdit) {
        accessDenied("Cannot cancel tool call");
      }

      const message = await tx.run(
        zql.agentSessionMessage
          .where("id", "=", args.messageId)
          .where("sessionId", "=", args.sessionId)
          .where("role", "=", "assistant")
          .one(),
      );
      if (!message) {
        accessDenied("Message not found");
      }

      let toolCallFound = false;
      const newParts: typeof message.parts = [];
      for (const part of message.parts) {
        if (isAgentToolPart(part) && part.toolCallId === args.toolCallId) {
          toolCallFound = true;
          if (part.state !== "input-available") {
            throw new Error("Tool call is not in input-available state");
          }
          newParts.push(toOutputCanceledToolPart(part));
        } else {
          newParts.push(part);
        }
      }

      if (!toolCallFound) {
        accessDenied("Tool call not found");
      }

      await tx.mutate.agentSessionMessage.update({
        id: args.messageId,
        parts: newParts,
        timeUpdated: timeNow,
      });
    },
  );

  /**
   * Check if a user can edit a session. Returns the result along with session info.
   * Used by API routes that need to verify access before performing operations.
   */
  export const canEditSession = async ({
    tx,
    ctx,
    args: { agentType, sessionId },
  }: {
    tx: Transaction;
    ctx: Context;
    args: { agentType: string; sessionId: string };
  }): Promise<{ canEdit: boolean; session: RawInfo | null }> => {
    const session = await tx.run(zql.agentSession.where("id", "=", sessionId).one());
    if (!session) {
      return { canEdit: false, session: null };
    }

    if (session.agentType !== agentType) {
      return { canEdit: false, session: normalizeSession(session) };
    }

    const agent = getAgentForSessionVersion(ctx.agentRegistry, agentType, session.agentVersion);
    const canEdit = await agent.canEdit?.({
      tx,
      ctx,
      args: { sessionId },
    });

    return { canEdit: canEdit ?? false, session: normalizeSession(session) };
  };
}
